import { Badge, Banner, Button, Field, Input, Select } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { ICONS } from '../components/ui/icons';

interface Field { id: string; name: string; key: string; type: string; description: string | null; filled: number }
interface FieldType { value: string; label: string }

const slug = (name: string) => name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);

/** Contatos › Campos personalizados */
export function CustomFieldsPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const canEdit = can('contacts:write');
  const [fields, setFields] = useState<Field[] | null>(null);
  const [types, setTypes] = useState<FieldType[]>([]);
  const [editing, setEditing] = useState<Field | 'new' | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('text');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Field | null>(null);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/custom-fields');
    if (!res.ok) return;
    const d = await res.json();
    setFields(d.fields);
    setTypes(d.types);
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const open = (f: Field | 'new') => {
    setEditing(f);
    setName(f === 'new' ? '' : f.name);
    setType(f === 'new' ? 'text' : f.type);
    setDescription(f === 'new' ? '' : f.description || '');
  };

  const save = async () => {
    setSaving(true);
    const isNew = editing === 'new';
    const res = await fetchApi(isNew ? '/api/custom-fields' : `/api/custom-fields/${(editing as Field).id}`, {
      method: isNew ? 'POST' : 'PATCH',
      body: JSON.stringify(isNew ? { name, type, description } : { name, description }),
    });
    const j = await res.json().catch(() => ({}));
    setSaving(false);
    if (!res.ok) { toast.error(j.message || 'Não foi possível salvar'); return; }
    toast.success(isNew ? 'Campo criado' : 'Campo atualizado');
    setEditing(null);
    load();
  };

  const remove = async () => {
    if (!deleting) return;
    const res = await fetchApi(`/api/custom-fields/${deleting.id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Campo excluído'); setDeleting(null); load(); } else toast.error('Não foi possível excluir');
  };

  const typeLabel = (t: string) => types.find((x) => x.value === t)?.label ?? t;

  if (editing !== null) {
    return (
      <FormPage
        backTo="/campos"
        backLabel="Campos"
        onBack={() => setEditing(null)}
        title={editing === 'new' ? 'Novo campo' : 'Editar campo'}
        description="Campos personalizados guardam dados extras dos contatos e viram variáveis nas mensagens."
        submitLabel={editing === 'new' ? 'Criar campo' : 'Salvar'}
        submitting={saving}
        submitDisabled={!name.trim()}
        onSubmit={save}
        onCancel={() => setEditing(null)}
      >
        <FormSection title="Detalhes">
          <Field label="Nome" description={editing === 'new' && name ? `Variável: {{${slug(name)}}}` : undefined}>
            <Input onChange={(e) => setName(e.target.value)} value={name} placeholder="Ex.: Cidade" />
          </Field>
          <Field label="Tipo" description={editing !== 'new' ? 'O tipo não muda depois de criado' : undefined}>
            <Select value={(types.find((t) => t.value === type) ?? null)?.value ?? null} items={types.map((t) => ({ value: t.value, label: t.label }))} onValueChange={(value: string | null) => {
                if (value) setType(value);
              }} disabled={editing !== 'new'} />
          </Field>
          <Field label="Descrição (opcional)">
            <Input onChange={(e) => setDescription(e.target.value)} value={description} />
          </Field>
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }} className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Campos personalizados</h2>
          <div style={{ color: 'var(--text-muted)' }}>
            Informações extras dos contatos (cidade, plano, data da compra…). Use nas mensagens como <code>{'{{chave}}'}</code>, filtre em Segmentos
            e preencha pela importação (coluna com o nome ou a chave do campo), pela API ou no cadastro do contato.
          </div>
        </div>
        {canEdit && <Button variant="primary" icon={ICONS.Plus} onClick={() => open('new')}>Novo campo</Button>}
      </div>

      <div className="page-content space-y-3">
        {fields && fields.length === 0 && <Banner variant="default" description={<>Nenhum campo ainda. Crie o primeiro para guardar informações que não cabem no cadastro padrão.</>} />}
        {fields?.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 20px' }}>
            <div style={{ minWidth: 0 }}>
              <div className="flex items-center gap-2">
                <div className="font-semibold">{f.name}</div>
                <Badge variant="neutral">{typeLabel(f.type)}</Badge>
              </div>
              <div className="text-xs !text-[var(--text-muted)]">
                <code>{`{{${f.key}}}`}</code> · preenchido em {f.filled.toLocaleString('pt-BR')} contato{f.filled === 1 ? '' : 's'}
                {f.description ? ` · ${f.description}` : ''}
              </div>
            </div>
            {canEdit && (
              <div className="flex gap-1" style={{ flexShrink: 0 }}>
                <Button variant="ghost" shape="square" icon={ICONS.Edit} aria-label={`Editar ${f.name}`} onClick={() => open(f)} />
                <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Excluir ${f.name}`} onClick={() => setDeleting(f)} />
              </div>
            )}
          </div>
        ))}
      </div>



      <DangerConfirmModal
        open={!!deleting}
        title="Excluir campo"
        confirmText="Excluir"
        objectName={deleting?.name}
        onCancel={() => setDeleting(null)}
        onConfirm={remove}
      >
        O campo <strong>{deleting?.name}</strong> e os valores preenchidos em {deleting?.filled ?? 0} contatos são apagados. Mensagens que usam{' '}
        <code>{`{{${deleting?.key}}}`}</code> passam a sair com esse trecho vazio. Não dá para desfazer.
      </DangerConfirmModal>
    </>
  );
}
